import type { SdiRate } from './formats'

/**
 * How far a cable will carry a signal, by class of cable and data rate.
 *
 * These are TYPICAL PUBLISHED FIGURES FOR A CLASS, not the spec of any one
 * product. A grade names the kind of cable — thin patch coax, standard RG6
 * broadcast coax, low-loss — and the numbers are the sort of distance
 * manufacturers quote for that class. Every one is stated as approximate in
 * the UI, and a specific product may do better or worse.
 *
 * That distinction matters. A reach limit the app states confidently and
 * wrongly is worse than one it admits is a guide: the first sends a crew out
 * with cable that will not work, the second makes them check.
 *
 * The shape of the table is the useful part, and it is not controversial:
 * every doubling of data rate roughly halves the distance, which is why a
 * 90 m camera run that has been fine for years fails the day a show goes UHD.
 */

export const CABLE_GRADES = ['PATCH', 'STANDARD', 'LOW_LOSS', 'FIBRE'] as const
export type CableGrade = (typeof CABLE_GRADES)[number]

export type GradeInfo = {
  key: CableGrade
  label: string
  /** What this class actually is, in the words a hire company would use. */
  detail: string
  /** Approximate reach in metres per rate. Null where it is not the limit. */
  reach: Record<SdiRate, number | null>
}

export const GRADE_INFO: Record<CableGrade, GradeInfo> = {
  PATCH: {
    key: 'PATCH',
    label: 'Patch / thin coax',
    detail: 'RG59-class, around 23 AWG — the thin stuff for patch panels and short tie-lines.',
    reach: { SD: 200, HD: 120, '3G': 70, '6G': 40, '12G': 25 },
  },
  STANDARD: {
    key: 'STANDARD',
    label: 'Standard broadcast coax',
    detail: 'RG6-class, around 18 AWG. The default drum on most trucks.',
    reach: { SD: 400, HD: 200, '3G': 120, '6G': 85, '12G': 45 },
  },
  LOW_LOSS: {
    key: 'LOW_LOSS',
    label: 'Low-loss / 12G-rated coax',
    detail: 'Heavier RG7/RG11-class, sold specifically for long UHD runs.',
    reach: { SD: 700, HD: 350, '3G': 220, '6G': 150, '12G': 100 },
  },
  FIBRE: {
    key: 'FIBRE',
    label: 'Fibre',
    detail: 'Distance stops being the constraint; the transceiver budget takes over.',
    reach: { SD: null, HD: null, '3G': null, '6G': null, '12G': null },
  },
}

export const DEFAULT_GRADE: CableGrade = 'STANDARD'

/** Approximate reach for a grade at a rate, or null where distance is not the limit. */
export function reachFor(grade: CableGrade, rate: SdiRate): number | null {
  return GRADE_INFO[grade]?.reach[rate] ?? null
}

/**
 * HDMI is graded by what the cable is certified to carry rather than by rate,
 * and passive copper runs out far sooner than people expect.
 */
export const HDMI_GRADES = ['HIGH_SPEED', 'PREMIUM', 'ULTRA', 'ACTIVE_OPTICAL'] as const
export type HdmiGrade = (typeof HDMI_GRADES)[number]

export const HDMI_GRADE_INFO: Record<HdmiGrade, { label: string; detail: string; reachM: number }> = {
  HIGH_SPEED: {
    label: 'High Speed (1080p / 4K30)',
    detail: 'Up to 10.2 Gb/s. Fine for HD; struggles at 4K60.',
    reachM: 15,
  },
  PREMIUM: {
    label: 'Premium High Speed (4K60)',
    detail: 'Certified to 18 Gb/s. Passive copper gets short quickly at this rate.',
    reachM: 5,
  },
  ULTRA: {
    label: 'Ultra High Speed (8K / 4K120)',
    detail: 'Certified to 48 Gb/s. Passive runs are very short.',
    reachM: 3,
  },
  ACTIVE_OPTICAL: {
    label: 'Active optical (AOC)',
    detail: 'Fibre inside an HDMI jacket. Directional — the source end is marked.',
    reachM: 100,
  },
}

export const DEFAULT_HDMI_GRADE: HdmiGrade = 'HIGH_SPEED'
